import type { MouseEvent, PropsWithChildren } from "react"
import { forwardRef, useCallback, useEffect, useRef, useState } from "react"

import ImageNotSupportedOutlinedIcon from "@mui/icons-material/ImageNotSupportedOutlined"
import { Box, Fade, SvgIcon as MUISvgIcon, Slide, Stack, Typography } from "@mui/material"
import { styled } from "@mui/material/styles"
import type { EmblaCarouselType } from "embla-carousel"
import useEmblaCarousel from "embla-carousel-react"
import { InView } from "react-intersection-observer"

import { gsap, useGSAP } from "@ggs/ui/local/gsap"
import type { ButtonProps, FC, FCWC, ResponsiveImageType } from ".."
import { Button, Container, Link, ResponsiveImage, Tag, WishlistButton } from ".."
import type { LinkType } from "../../graphql/types"

export type TeaserGridItemProps = {
  image: ResponsiveImageType
  tag: string
  article_type?: {
    title: string
    uri?: string
  }
  title: string
  description: string
  uri: string
  nid?: number
  contentType?: string
  onLike?: (arg: { contentType?: string; nid?: number }, arg2?: string) => void
  onUnlike?: (arg: { contentType?: string; nid?: number }, arg2?: string) => void
  liked?: boolean
}

type Teaser3UpProps = {
  title: string
  subtitle: string
  link?: LinkType
  items: TeaserGridItemProps[]
  onLike?: (arg: { contentType?: string; nid?: number }, arg2?: string) => void
  onUnlike?: (arg: { contentType?: string; nid?: number }, arg2?: string) => void
}

const Teaser: FC<Teaser3UpProps> = ({ title, subtitle, link, items, onLike, onUnlike }) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const scrollTrackRef = useRef<HTMLDivElement>(null)
  const scrollThumbRef = useRef<HTMLDivElement>(null)
  const [thumbWidth, setThumbWidth] = useState(20)
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([])

  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start",
    containScroll: "trimSnaps",
    breakpoints: {
      "(min-width: 1080px)": {
        active: false,
      },
    },
  })

  const onInit = useCallback(
    (emblaApi: EmblaCarouselType) => {
      const snaps = emblaApi?.scrollSnapList()
      setScrollSnaps(snaps)

      if (containerRef.current && scrollTrackRef.current) {
        const { clientWidth, scrollWidth } = containerRef.current
        const { clientWidth: trackSize } = scrollTrackRef.current
        setThumbWidth(Math.max((clientWidth / scrollWidth) * trackSize, 20))
      }
    },
    [containerRef, scrollTrackRef]
  )

  const onScroll = useCallback(
    (emblaApi: EmblaCarouselType) => {
      if (!scrollTrackRef.current || !scrollThumbRef.current) return

      const progress = emblaApi.scrollProgress()
      const { clientWidth: trackWidth } = scrollTrackRef.current
      const scrollX = progress * (trackWidth - thumbWidth)
      const thumb = scrollThumbRef.current
      thumb.style.left = `${scrollX}px`
    },
    [thumbWidth]
  )

  const handleTrackClick = useCallback(
    (e: MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()

      const { current: trackCurrent } = scrollTrackRef
      const { current: contentCurrent } = containerRef

      if (!emblaApi || !trackCurrent || !contentCurrent) return

      const { clientX } = e
      const target = e.target as HTMLDivElement
      const rect = target.getBoundingClientRect()
      const trackLeft = rect.left
      const clickRatio = (clientX - trackLeft) / trackCurrent.clientWidth

      const closestSlide = scrollSnaps.reduce((prev, curr) => {
        return Math.abs(curr - clickRatio) < Math.abs(prev - clickRatio) ? curr : prev
      }, 0)
      const closestIndex = scrollSnaps.indexOf(closestSlide)
      emblaApi?.scrollTo(closestIndex)
    },
    [scrollSnaps, scrollTrackRef, containerRef]
  )

  useEffect(() => {
    if (!emblaApi) return

    onInit(emblaApi)
    emblaApi.on("reInit", onInit)
    emblaApi.on("resize", onInit)
    emblaApi.on("scroll", onScroll)
  }, [emblaApi, containerRef, scrollTrackRef, onInit, onScroll])

  return (
    <TeaserStylesSection>
      <Container maxWidth={1300}>
        <AnimatedSvgIcon />

        <TeaserStylesHeader rowGap={5} mb={{ xs: 10, md: 15 }} position="relative" zIndex={1}>
          <TeaserHeaderSubtitle>{subtitle}</TeaserHeaderSubtitle>
          <TeaserHeaderTitle>{title}</TeaserHeaderTitle>
        </TeaserStylesHeader>
        {link?.title && <TeaserCTA href={link.uri}>{link.title}</TeaserCTA>}

        {items && (
          <Box
            ref={emblaRef}
            overflow="hidden"
            mt={{ xs: 20, md: 22.5 }}
            sx={(theme) => ({
              [theme.breakpoints.down(1080)]: {
                cursor: "pointer",
              },
            })}
          >
            <TeaserItems ref={containerRef}>
              {items.map((item, i) => (
                <InView key={item?.title} triggerOnce>
                  {({ inView, ref }) => (
                    <Box ref={ref}>
                      <Slide
                        direction="up"
                        in={inView}
                        container={containerRef.current}
                        timeout={700 * (i + 1)}
                      >
                        <Box>
                          <Fade in={inView} timeout={700 * (i + 1)}>
                            <TeaserGridItem onLike={onLike} onUnlike={onUnlike} {...item} />
                          </Fade>
                        </Box>
                      </Slide>
                    </Box>
                  )}
                </InView>
              ))}
            </TeaserItems>
          </Box>
        )}

        <Box
          width="100%"
          height="2px"
          mt={{ xs: 10, md: 15 }}
          position="relative"
          sx={(theme) => ({
            [theme.breakpoints.up(1080)]: {
              display: "none",
            },
          })}
        >
          <Box
            ref={scrollTrackRef}
            onClick={(e) => handleTrackClick(e)}
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            bgcolor={(theme) => theme.palette["neutral.200"].main}
            sx={{ "&:hover": { cursor: "pointer" } }}
          />
          <Box
            ref={scrollThumbRef}
            position="absolute"
            width={thumbWidth}
            top={0}
            left={0}
            bottom={0}
            bgcolor={(theme) => theme.palette["primary.800"].main}
          />
        </Box>
      </Container>
    </TeaserStylesSection>
  )
}

export default Teaser

/** A sub-component for the Teaser Items wrapper */
const TeaserItems = forwardRef<HTMLDivElement, PropsWithChildren>(({ children }, ref) => (
  <Box
    ref={ref}
    display="grid"
    columnGap={{ xs: 5, md: 15 }}
    gridAutoFlow="column"
    gridAutoColumns="minmax(294px, auto)"
  >
    {children}
  </Box>
))

TeaserItems.displayName = "TeaserItems"

/** A sub-component for the teaser CTA button. */
const TeaserCTA: FC<ButtonProps> = (props) => (
  <Box
    mt={{ xs: 0, md: 15 }}
    sx={(theme) => ({
      [theme.breakpoints.down("md")]: {
        textAlign: "center",
      },
    })}
  >
    <Button variant="default" {...props} />
  </Box>
)

/** A sub-component for the teaser header title. */
const TeaserHeaderTitle: FCWC = ({ children }) => (
  <Typography
    variant="heading.2xl"
    component="h2"
    textAlign={{ xs: "center", md: "left" }}
    color={(theme) => theme.palette["neutral.700"].main}
    maxWidth={{ md: 600 }}
  >
    {children}
  </Typography>
)

/** A sub-component for the teaser header subtitle. */
const TeaserHeaderSubtitle: FCWC = ({ children }) => (
  <Typography
    variant="body.lg"
    component="h3"
    color={(theme) => theme.palette["neutral.700"].main}
    textAlign={{ xs: "center", md: "left" }}
  >
    {children}
  </Typography>
)

const TeaserGridItem = forwardRef<HTMLDivElement, TeaserGridItemProps>(
  (
    {
      image,
      tag,
      article_type,
      title,
      description,
      uri,
      onLike,
      onUnlike,
      contentType,
      nid,
      liked,
    },
    ref
  ) => {
    const id = nid && Array.isArray(nid) ? nid[0] : nid
    return (
      <Box
        ref={ref}
        position="relative"
        sx={{
          "&:hover": {
            img: {
              transform: "scale(1.05)",
            },
          },
        }}
      >
        <Box
          position="relative"
          overflow="hidden"
          borderRadius={1.5}
          mb={{ xs: 6, md: 10 }}
          sx={{
            aspectRatio: "1",
          }}
        >
          {image ? (
            <ResponsiveImage
              image={image}
              borderRadius={1.5}
              sx={{
                display: "block",
                aspectRatio: "1",
                width: "100%",
                border: (theme) => `1px solid ${theme.palette["neutral.100"].main}`,
                transition: (theme) => theme.transitions.create("transform"),
              }}
            />
          ) : (
            <Stack
              alignItems="center"
              justifyContent="center"
              bgcolor={(theme) => theme.palette["secondary.300"].main}
              borderRadius={1.5}
              sx={{ width: 380, height: 380 }}
            >
              <ImageNotSupportedOutlinedIcon />
            </Stack>
          )}
          <Link type="overlay" href={uri} aria-label={title} title={title} />
        </Box>

        <TeaserItemOverlayStyle
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
        >
          {(article_type?.title || tag) && (
            <Tag
              color="white"
              label={article_type?.title ? article_type?.title : tag}
              uri={article_type?.uri}
              autoWidth
              sx={{ zIndex: 1 }}
            />
          )}
          <WishlistButton
            sx={{ zIndex: 1 }}
            liked={liked}
            onLike={() => (onLike ? onLike({ contentType, nid: id }) : null)}
            onUnlike={() => (onUnlike ? onUnlike({ contentType, nid: id }) : null)}
          />
        </TeaserItemOverlayStyle>
        <TeaserItemTitle title={title} uri={uri} />
        <TeaserItemDescription>{description}</TeaserItemDescription>
        <TeaserItemLink uri={uri} />
      </Box>
    )
  }
)
TeaserGridItem.displayName = "TeaserGridItem"

/** A sub-component for the title in teaser item */
const TeaserItemTitle: FC<LinkType> = ({ title, uri }) => (
  <Link
    variant="heading.sm"
    type="base"
    mb={{ xs: 3, md: 5 }}
    sx={{ display: "inline-block" }}
    href={uri}
  >
    {title}
  </Link>
)

/** A sub-component for the descripton in teaser item */
const TeaserItemDescription: FCWC = ({ children }) => (
  <Typography variant="body.md" component="h3" mb={{ xs: 5, md: 10 }}>
    {children}
  </Typography>
)

/** A sub-component for the link in teaser item  */
const TeaserItemLink: FC<{ uri: string }> = ({ uri }) => (
  <Box mt={5} mb={5}>
    <Link href={uri} variant="link.md">
      Read More
    </Link>
  </Box>
)

/** A sub-component for the animated svg in the bg  */
const AnimatedSvgIcon = () => {
  const mainRef = useRef<HTMLDivElement>(null)
  const pathRef = useRef<SVGPathElement>(null)
  const pointRef = useRef<SVGGElement>(null)

  useGSAP(
    () => {
      gsap.to(pathRef.current, {
        strokeDashoffset: 786,
        scrollTrigger: {
          trigger: mainRef.current,
          start: "top 75%",
          end: "+=200",
          scrub: true,
          invalidateOnRefresh: true,
        },
      })
      gsap.to(pointRef.current, {
        opacity: 1,
        scrollTrigger: {
          trigger: mainRef.current,
          start: "bottom 83%",
          end: "+=100",
          scrub: true,
          invalidateOnRefresh: true,
        },
      })
    },
    {
      scope: mainRef,
      dependencies: [mainRef, pathRef, pointRef],
    }
  )

  return (
    <Box
      ref={mainRef}
      sx={(theme) => ({
        width: 769,
        height: 260,
        position: "absolute",
        top: 53,
        display: { xs: "none", lg: "block" },
        overflow: "hidden",

        [theme.breakpoints.down(1718)]: {
          left: 949,
        },
        [theme.breakpoints.up(1718)]: {
          right: 0,
        },
      })}
    >
      <MUISvgIcon
        viewBox="0 0 770 261"
        sx={{
          width: 770,
          height: 261,
        }}
      >
        <path
          d="M769.308 11.979C723.059 7.854 674.88-2.106 629.461 3.58c-62.216 7.79-98.071 37.26-129.645 68.362-22.447 22.11-47.689 43.193-71.796 64.582-31.104 27.598-62.246 55.027-108.858 73.633-46.826 18.692-104.25 26.11-160.306 31.055-32.159 2.836-70.358 3.452-103.415.504"
          fill="none"
          stroke="#F05B2A"
          strokeWidth="2"
          strokeDasharray=".1 10"
          strokeLinecap="round"
        />
        <path
          ref={pathRef}
          d="M769.308 11.979C723.059 7.854 674.88-2.106 629.461 3.58c-62.216 7.79-98.071 37.26-129.645 68.362-22.447 22.11-47.689 43.193-71.796 64.582-31.104 27.598-62.246 55.027-108.858 73.633-46.826 18.692-104.25 26.11-160.306 31.055-32.159 2.836-70.358 3.452-103.415.504"
          fill="none"
          stroke="#FFFFFF"
          strokeWidth="5"
          strokeDasharray={786}
          strokeDashoffset={786 * 2}
        />
        <g ref={pointRef} opacity="0">
          <path
            fill="#FDEEE9"
            fillOpacity="0.4"
            d="M47.27 244.306c-4.413 12.499-18.122 19.053-30.621 14.64-12.499-4.413-19.053-18.123-14.64-30.621 4.413-12.499 18.123-19.053 30.622-14.64 12.498 4.413 19.053 18.123 14.64 30.621Z"
          />
          <path fill="#F05B2A" d="M29.212 238.13a4 4 0 1 1-7.544-2.665 4 4 0 0 1 7.544 2.665Z" />
        </g>
      </MUISvgIcon>
    </Box>
  )
}

/** styled components */
const TeaserStylesSection = styled("section")(
  ({ theme }) => `
    width: 100%;
    padding: ${theme.spacing(12, 0)};
    position: relative;
    overflow-x: hidden;

    ${theme.breakpoints.up("md")} {
      padding: ${theme.spacing(24, 0)};
    }
  `
)

const TeaserStylesHeader = styled(Stack)(
  ({ theme }) => `
    width: 100%;
    ${theme.breakpoints.down("md")} {
      align-items: center;
    }
  `
)

const TeaserItemOverlayStyle = styled(Stack)(
  ({ theme }) => `
    top: 0;
    left: 0;
    right: 0;
    padding: ${theme.spacing(3)};
    position: absolute;

    ${theme.breakpoints.up("md")} {
      padding: ${theme.spacing(5)};
    }
  `
)
